/* Implements Structured Course-Platform theme (Option 3 - Coursera/Udemy course catalog and course-detail pages feel) */
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthBridge';
import { Breadcrumbs } from './Breadcrumbs';
import { AdUnit } from './AdUnit';
import { ContinueLastQuizCard } from './ContinueLastQuizCard';
import { 
  BookOpen, HelpCircle, Award, Stethoscope, Sparkles, 
  Brain, Calculator, Bot, ArrowRight, CheckCircle2, 
  GraduationCap, Clock, Trophy, Flame, ChevronRight, UserCheck, ShieldCheck
} from 'lucide-react';

interface StructuredCourseDashboardProps {
  onNavigate?: (page: string) => void;
  standingData?: any;
}

interface FacultyMember {
  id: string;
  name: string;
  role: string;
  credentials: string;
  specialty: string;
  avatarUrl: string;
}

export const StructuredCourseDashboard: React.FC<StructuredCourseDashboardProps> = ({ 
  onNavigate,
  standingData 
}) => {
  const { user, activeCert } = useAuth();
  const [faculty, setFaculty] = useState<FacultyMember[]>([]);

  useEffect(() => {
    // Fetch faculty members or load fallback
    const fetchFaculty = async () => {
      try {
        const res = await fetch('/practice-exams/api/faculty');
        if (res.ok) {
          const data = await res.json();
          setFaculty(data.faculty || []);
        }
      } catch (e) {
        // Fallback
      }
    };
    fetchFaculty();
  }, []);

  const getStateFullName = (st?: string) => {
    if (!st || st === 'Global' || st === 'Universal') return '';
    const stateMap: Record<string, string> = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
      'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
      'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
      'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
      'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
      'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
      'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
      'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
    };
    return stateMap[st.toUpperCase()] || st;
  };

  const certFullName = activeCert?.name || 'Certified Nursing Assistant';
  const stateFullName = getStateFullName(user?.state);
  const isStateSpecific = Boolean(stateFullName);
  const fullCertTitle = isStateSpecific ? `${stateFullName} ${certFullName}` : certFullName;

  // Calculate user module progress metrics
  const quizzesTaken = user?.stats?.quizzesTaken || 0;
  const examsCompleted = user?.history?.length || 0;
  const highestScore = user?.history && user.history.length > 0 
    ? Math.max(...user.history.map((h: any) => h.score || 0)) 
    : 0;
  const streakDays = user?.stats?.streakDays || user?.stats?.streakCount || 0;
  const points = user?.stats?.points || 0;

  // Course module definition with sequential module numbering
  const certPrefix = activeCert?.id || 'cna';
  const modules = [
    {
      id: 'study-guide',
      number: 1,
      title: 'Clinical Theory & Core Syllabus',
      description: 'Comprehensive curriculum study modules, textbook units, and key nursing care topics.',
      icon: BookOpen,
      route: `/${certPrefix}/guide`,
      progress: Math.min(100, Math.round((quizzesTaken * 8) + 15)),
      status: quizzesTaken > 0 ? 'In Progress' : 'Not Started',
    },
    {
      id: 'practice-quiz',
      number: 2,
      title: 'Practice Quiz Drills',
      description: 'Targeted diagnostic question banks with instant rationales and domain categorizations.',
      icon: HelpCircle,
      route: `/${certPrefix}/practice`,
      progress: Math.min(100, Math.round(quizzesTaken * 12)),
      status: quizzesTaken > 2 ? 'Completed' : quizzesTaken > 0 ? 'In Progress' : 'Not Started',
    },
    {
      id: 'board-simulation',
      number: 3,
      title: 'Board Exam Simulation',
      description: 'Full timed licensing exam simulators adhering strictly to state certification test standards.',
      icon: Award,
      route: `/${certPrefix}/exam`,
      progress: Math.min(100, Math.round(examsCompleted * 20)),
      status: examsCompleted > 0 ? 'In Progress' : 'Not Started',
    },
    {
      id: 'skills-lab',
      number: 4,
      title: 'Clinical Skills Lab',
      description: 'Step-by-step physical care procedures, clinical checklists, and video demonstration guides.',
      icon: Stethoscope,
      route: `/${certPrefix}/skills`,
      progress: user?.stats?.skillsMastered ? Math.min(100, user.stats.skillsMastered * 10) : 0,
      status: 'In Progress',
    },
    {
      id: 'bedside-scenarios',
      number: 5,
      title: 'Bedside Scenario Lab',
      description: 'Interactive clinical judgment cases, decision paths, and real patient care scenarios.',
      icon: Brain,
      route: `/${certPrefix}/scenario`,
      progress: user?.history ? Math.min(100, user.history.filter((h: any) => h.examMode?.includes('Scenario')).length * 25) : 0,
      status: (user?.history?.filter((h: any) => h.examMode?.includes('Scenario')).length || 0) > 0 ? 'In Progress' : 'Not Started',
    },
    {
      id: 'flashcards',
      number: 6,
      title: 'Medical Terminology Flashcards',
      description: 'Spaced-repetition term mastery, medical abbreviations, and rapid recall drills.',
      icon: Sparkles,
      route: `/${certPrefix}/flashcards`,
      progress: user?.flashcardSRS ? Math.min(100, Object.keys(user.flashcardSRS).length * 10) : 0,
      status: user?.flashcardSRS && Object.keys(user.flashcardSRS).length > 0 ? 'In Progress' : 'Not Started',
    },
    {
      id: 'math-lab',
      number: 7,
      title: 'Clinical Math & Dosage Lab',
      description: 'Dosage calculations, fluid intake/output measurements, and medical arithmetic.',
      icon: Calculator,
      route: `/${certPrefix}/practice`,
      progress: user?.stats?.mathLabCompleted ? Math.min(100, user.stats.mathLabCompleted * 20) : 0,
      status: user?.stats?.mathLabCompleted ? 'In Progress' : 'Not Started',
    },
    {
      id: 'ai-tutor',
      number: 8,
      title: 'AI Clinical Tutor',
      description: `24/7 interactive AI study companion for ${activeCert?.shortName || 'board'} curriculum inquiries.`,
      icon: Bot,
      route: `/${certPrefix}/tutor`,
      progress: user?.stats?.aiQueriesCount ? Math.min(100, user.stats.aiQueriesCount * 10) : 0,
      status: 'Available 24/7',
    },
  ];

  // Calculate overall pathway progress
  const completedModulesCount = modules.filter(m => m.progress >= 80).length;
  const inProgressModulesCount = modules.filter(m => m.progress > 0 && m.progress < 80).length;
  const avgProgress = Math.round(modules.reduce((acc, m) => acc + m.progress, 0) / modules.length);

  const fallbackFaculty: FacultyMember[] = [
    {
      id: 'f1',
      name: 'Dr. Evelyn Vance, DNP, RN',
      role: 'Head of Clinical Nursing Curriculum',
      credentials: 'DNP, MSN, RN (20+ yrs Nursing Education)',
      specialty: 'Clinical Skills & Board Standards',
      avatarUrl: 'https://images.unsplash.com/photo-1594824813572-9d0426f8b668?auto=format&fit=crop&q=80&w=200'
    },
    {
      id: 'f2',
      name: 'Marcus Thorne, MSN, RN, CEN',
      role: 'Senior Exam Simulation Director',
      credentials: 'MSN, BSN, RN, Emergency Care Specialist',
      specialty: 'Patient Safety & Care Scenarios',
      avatarUrl: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&q=80&w=200'
    }
  ];

  const displayFaculty = faculty.length > 0 ? faculty : fallbackFaculty;

  return (
    <div className="space-y-8 animate-fade-in pb-24 max-w-7xl mx-auto text-slate-900 dark:text-slate-100">
      
      {/* Continue In-Progress Quiz / Exam Banner */}
      {onNavigate && <ContinueLastQuizCard onNavigate={onNavigate} />}

      {/* HERO SECTION - COURSE CATALOG HEADER + RIGHT AD */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        <div className="xl:col-span-8 bg-[#1D4ED8] text-white p-6 md:p-10 rounded-2xl border border-blue-800 shadow-sm relative overflow-hidden flex flex-col justify-between">
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="space-y-4 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 bg-white/15 text-white rounded-md text-[10px] font-extrabold uppercase tracking-widest border border-white/20">
                  Course Catalog • {fullCertTitle}
                </span>
                <span className="px-3 py-1 bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 rounded-md text-[10px] font-extrabold uppercase tracking-widest flex items-center gap-1.5">
                  <CheckCircle2 size={12} />
                  {isStateSpecific ? `${stateFullName} Board Standard Verified` : 'Universal Board Standard Verified'}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Clinical Study Pathway
              </h1>
              
              <p className="text-blue-100 text-sm md:text-base leading-relaxed font-normal">
                Welcome back, <strong className="text-white font-bold">{user?.name?.split(' ')[0] || 'Learner'}</strong>! You are enrolled in the official <strong className="text-white font-bold">{fullCertTitle}</strong> curriculum. Complete each numbered module sequentially to prepare for your licensing board exam.
              </p>
            </div>

            {/* Overall Pathway Progress Card */}
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 w-full lg:w-72 shrink-0 space-y-4">
              <div className="flex items-center justify-between border-b border-white/15 pb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-100 flex items-center gap-1.5">
                  <GraduationCap size={16} /> Pathway Progress
                </span>
                <span className="text-xs font-extrabold text-white bg-white/20 px-2 py-0.5 rounded">
                  {avgProgress}% Complete
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-xs font-semibold text-blue-100">
                  <span>Modules Complete</span>
                  <span className="font-bold text-white">{completedModulesCount} of {modules.length}</span>
                </div>
                
                {/* Single Brand Color Progress Bar */}
                <div className="h-2.5 w-full bg-blue-950/60 rounded-full overflow-hidden p-0.5 border border-white/20">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-700" 
                    style={{ width: `${avgProgress}%` }} 
                  />
                </div>

                <p className="text-[11px] text-blue-100 font-medium pt-1">
                  {inProgressModulesCount > 0 ? `${inProgressModulesCount} modules currently in progress.` : 'Ready to begin Module 1.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dedicated Right Column for Ad Unit outside hero banner */}
        <div className="xl:col-span-4 flex flex-col justify-start">
          <AdUnit placement="dashboard_welcome_right" />
        </div>
      </div>

      {/* CLINICAL STUDY PATHWAY MODULE LIST (COURSE CATALOG GRID) */}
      <section className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
          <div>
            <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
              Curriculum Syllabus & Learning Units
            </h2>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1">
              Select a module to continue your structured learning pathway.
            </p>
          </div>
          <span className="text-xs font-bold text-[#1D4ED8] dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 self-start sm:self-auto">
            8 Sequential Modules
          </span>
        </div>

        {/* Course Catalog Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {modules.map((mod) => {
            const IconComponent = mod.icon;
            return (
              <div 
                key={mod.id}
                onClick={() => onNavigate && onNavigate(mod.route)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs hover:border-[#1D4ED8] dark:hover:border-blue-500 transition-all cursor-pointer group flex flex-col justify-between space-y-5"
              >
                <div className="space-y-3">
                  {/* Module Header Badge & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="px-2.5 py-1 bg-blue-50 dark:bg-blue-950 text-[#1D4ED8] dark:text-blue-300 border border-blue-200 dark:border-blue-800 text-[10px] font-extrabold uppercase tracking-wider rounded-md">
                      MODULE {mod.number} OF {modules.length}
                    </span>

                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                      mod.progress >= 80 
                        ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' 
                        : mod.progress > 0 
                        ? 'bg-blue-50 dark:bg-blue-950 text-[#1D4ED8] dark:text-blue-300 border border-blue-200 dark:border-blue-800' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
                    }`}>
                      {mod.status}
                    </span>
                  </div>

                  {/* Module Title & Description */}
                  <div className="flex items-start gap-3 pt-1">
                    <div className="p-2.5 bg-[#1D4ED8] text-white rounded-xl shrink-0 group-hover:scale-105 transition-transform">
                      <IconComponent size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 group-hover:text-[#1D4ED8] dark:group-hover:text-blue-400 transition-colors">
                        Module {mod.number}: {mod.title}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-normal leading-relaxed mt-1">
                        {mod.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar & CTA */}
                <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                      <span>Module Completion</span>
                      <span className="font-bold text-[#1D4ED8] dark:text-blue-400">{mod.progress}%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#1D4ED8] transition-all duration-500" 
                        style={{ width: `${mod.progress}%` }} 
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                      <Clock size={12} /> Standard Syllabus Unit
                    </span>
                    <button className="px-4 py-2 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-xs group-hover:translate-x-0.5">
                      <span>{mod.progress > 0 ? 'Continue' : 'Start Module'}</span>
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FACULTY SPOTLIGHT SECTION */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 space-y-6 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
          <div className="space-y-0.5">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <UserCheck size={18} className="text-[#1D4ED8]" /> Meet the faculty behind your questions
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Curriculum reviewed and authored by licensed nursing educators.
            </p>
          </div>
          <button
            onClick={() => onNavigate && onNavigate('faculty')}
            className="text-xs font-bold text-[#1D4ED8] hover:text-blue-700 bg-blue-50 dark:bg-blue-950 px-3 py-1.5 rounded-md border border-blue-200 dark:border-blue-800 transition-all cursor-pointer"
          >
            Board Faculty &rarr;
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayFaculty.map((member) => (
            <div key={member.id} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700/80">
              <img 
                src={member.avatarUrl} 
                alt={member.name} 
                className="w-14 h-14 rounded-full object-cover border-2 border-[#1D4ED8] shrink-0" 
              />
              <div className="space-y-0.5">
                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">{member.name}</h4>
                <p className="text-xs text-[#1D4ED8] font-semibold">{member.role}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">{member.credentials}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECONDARY STAT ROW (Visually secondary, non-hero level) */}
      <section className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Secondary Engagement Metrics
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Study Streak</span>
            <div className="text-xl font-bold text-[#1D4ED8] flex items-center gap-1 mt-1">
              <Flame size={18} /> {streakDays} Days
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Completed Exams</span>
            <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-1">
              {examsCompleted} Sets
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Highest Exam Score</span>
            <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {highestScore > 0 ? `${highestScore}%` : '—'}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Learning Points</span>
            <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-1">
              {points} pts
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Bottom Ad Placement */}
      <div className="w-full pt-4">
        <AdUnit placement="dashboard_bottom" />
      </div>

    </div>
  );
};
